import React, { useEffect, useRef, useState } from "react";
import FlexBox from "schoolUiComponents/basic/Box/FlexBox";
import { Colors } from "baseStyles/enums/colors";
import Text from "schoolUiComponents/basic/Text";
import { Banner } from "schoolUiComponents/Banner";
import PositionBox from "schoolUiComponents/basic/Box/PositionBox";
import { history } from "../history";
import Input from "schoolUiComponents/basic/Input";
import ForgetPasswordStyles from "./style";
import Box from "schoolUiComponents/basic/Box";
import Button from "schoolUiComponents/basic/Button";
import { APIS } from "../api";
import { InputTypes } from "schoolUiComponents/basic/Input/types";
import PasswordIcon from "../assets/passwordIcon.svg";
import PasswordShowIcon from "../assets/passwordShowIcon.svg";
import { interactedChangePasswordScreenEvent, viewedChangePasswordScreenEvent } from "../events";
import { REFERRER_PAGE_MAP } from "../../utility/referrer";
import { IReferralData } from "../MergeStudent";
import { SuccessPopup } from "./successPopup";

enum VerificationType {
    New = 'new',
    Confirm = 'confirm'
}

const PasswordInput = ({ ...props }) => {
    const [passwordVisibility, togglePasswordVisibility] = useState(false);

    return (
        <Input
            type={passwordVisibility ? InputTypes.TEXT : InputTypes.PASSWORD}
            padding={"16px 24px"}
            customPlaceholder
            icon={<FlexBox height={24} onClick={() => togglePasswordVisibility(visible => !visible)}>{passwordVisibility ? <PasswordShowIcon /> : <PasswordIcon />}</FlexBox>}
            fontSize={16}
            lineHeight={24}
            fontWeight={600}
            {...props}
        />
    );
}

export const ResetPassword = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const [newPassword, setNewPassword] = useState<string | null>();
    const [confirmPassword, setConfirmPassword] = useState<string | null>();
    const [error, setError] = useState(null);
    const [confirmError, setConfirmError] = useState(null);
    const [showSuccessPopup, toggleSuccessPopup] = useState(false);
    const styles = ForgetPasswordStyles();
    const [inputMethod, setInputMethod] = useState<Record<VerificationType, string>>({ [VerificationType.New]: '', [VerificationType.Confirm]: '' });
    const [isTyping, setIsTyping] = useState<Record<VerificationType, boolean>>({ [VerificationType.New]: false, [VerificationType.Confirm]: false });
    const [referralData, setReferralData] = useState<IReferralData>({ referrerPage: "", invitationSource: "" });

    useEffect(() => {
        const referrerPage = REFERRER_PAGE_MAP[queryParams.get("referrer_code")] || "default";
        const invitationSource = queryParams.get("referral_type") || "";
        setReferralData({ referrerPage, invitationSource });
        viewedChangePasswordScreenEvent({ referrerPage: referrerPage, invitationSource: invitationSource })
    }, [])

    const isPasswordValid = (password: string) => {
        const pass = password?.trim();
        if (!pass?.length) {
            setError("Password fields shouldn't be empty");
            return false;
        }
        if (pass.length < 6) {
            setError("Too short, should be atleast 6 characters long");
            return false;
        }
        setError(null);
        return true;
    }

    const backHandler = () => {
        history.goBack();
        interactedChangePasswordScreenEvent({ clickedOn: "Back button" });
    }

    const handleResetNow = (event: any) => {
        event?.stopPropagation();
        event?.preventDefault();
        if (!isPasswordValid(newPassword)) {
            return;
        }
        if (newPassword != confirmPassword) {
            setConfirmError("New password and confirmation password must be same");
            return;
        }
        if (!isPasswordValid(confirmPassword)) {
            return;
        }
        interactedChangePasswordScreenEvent({ clickedOn: "Change password" });
        const queryParams = new URLSearchParams(window.location.search);
        const referral_type = queryParams.get("referral_type");
        const referrer_code = queryParams.get("referrer_code");
        const student_id = queryParams.get("student_id");
        const trd_id = queryParams.get("trd_id");
        const reset_password_token = queryParams.get("reset_password_token");
        APIS.changePassword({ password: newPassword?.trim(), password_confirmation: confirmPassword?.trim(), reset_password_token, referral_type, referrer_code, trd_id }).then((res) => {
            if (res.data.success) {
                toggleSuccessPopup(true);
                setTimeout(() => {
                    // window.location.href = res.data.redirect_url;
                    window.close();
                }, 4000);
            } else {
                setError(res?.data?.error_message || 'Something went wrong!');
                setTimeout(() => {
                    window.location.href = res.data.redirect_url;
                }, 2000);
            }
        }).catch((error) => {
            setError(error?.response?.data?.error_message || 'Something went wrong!')
        })
    }

    const sendEventOnPaste = (clickedOn: string, key: VerificationType) => {
        setInputMethod({ ...inputMethod, [key]: 'pasted' });
        interactedChangePasswordScreenEvent({ clickedOn, interactionDetails: "Copy pasted" });
    }

    const onChangeEmailEvent = (clickedOn: string, key: VerificationType) => {
        if (!isTyping?.[key] && (inputMethod?.[key] !== 'pasted')) {
            interactedChangePasswordScreenEvent({ clickedOn, interactionDetails: "Started typing" });
            setIsTyping({ ...isTyping, [key]: true });
        }
    }

    return (
        <FlexBox dataTestId="forget-password" fullWidth backgroundColor={Colors.WHITE} minHeight={`${window.innerHeight - 61}px`} column relative>
            {/* {(history.length > 2) && <PositionBox.absolute onClick={backHandler} left={34} top={36}><Text.H2 color={Colors.PURE_PURPLE} hoverColor={Colors.NAVY_BLUE}><i style={{ fontSize: '18px' }} className="iconcust-iconBackBtn"></i></Text.H2></PositionBox.absolute>} */}
            <FlexBox flex={1} center fullWidth>
                <form onSubmit={handleResetNow} autoComplete="on">
                    <FlexBox {...styles.mainBox} center column>
                        <Text.H1 center fontWeight={700}>Reset password</Text.H1>
                        <FlexBox fullWidth column gap={16}>
                            <FlexBox gap={8} margin={"8px 0 0 0"} column fullWidth alignStart>
                                <PasswordInput
                                    value={newPassword}
                                    placeholder={"New password"}
                                    tabIndex={2}
                                    onChange={(e: any) => {
                                        error && setError(null);
                                        confirmError && setConfirmError(null);
                                        setNewPassword(e.target?.value);
                                        onChangeEmailEvent("New password", VerificationType.New);
                                    }}
                                    onClick={() => interactedChangePasswordScreenEvent({ clickedOn: "New password" })}
                                    onPaste={() => sendEventOnPaste("New password", VerificationType.New)}
                                    name="sl-user-new-password"
                                    data-test-id="new-password"
                                    color={!newPassword?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                                    hasError={error}
                                    width={styles.mainBox.width}
                                />
                                {!!error?.length && <Box padding={"0 24px"} fullWidth><Text.SMALL color={Colors.DARK_RED_2} lines={3} fullWidth><span dangerouslySetInnerHTML={{ __html: error }} /></Text.SMALL></Box>}
                            </FlexBox>
                            <FlexBox gap={8} margin={"8px 0 0 0"} column fullWidth alignStart>
                                <PasswordInput
                                    value={confirmPassword}
                                    placeholder={"Confirm new password"}
                                    tabIndex={3}
                                    onChange={(e: any) => {
                                        confirmError && setConfirmError(null);
                                        setConfirmPassword(e.target?.value);
                                        onChangeEmailEvent("Confirm new password", VerificationType.Confirm);
                                    }}
                                    onClick={() => interactedChangePasswordScreenEvent({ clickedOn: "Confirm new password" })}
                                    onPaste={() => sendEventOnPaste("Confirm new password", VerificationType.Confirm)}
                                    name="sl-user-confirm-password"
                                    data-test-id="confirm-password"
                                    color={!confirmPassword?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                                    hasError={confirmError}
                                    width={styles.mainBox.width}
                                />
                                {!!confirmError?.length && <Box padding={"0 24px"} fullWidth><Text.SMALL color={Colors.DARK_RED_2} lines={3} fullWidth><span dangerouslySetInnerHTML={{ __html: confirmError }} /></Text.SMALL></Box>}
                            </FlexBox>
                        </FlexBox>
                        <Button primary onClick={handleResetNow} disabled={!newPassword?.length || !confirmPassword?.length}><Text.H4 color={Colors.WHITE}>Change password</Text.H4></Button>
                    </FlexBox>
                </form>
            </FlexBox>
            {showSuccessPopup && <SuccessPopup popupCloseCallback={() => toggleSuccessPopup(false)} referralData={referralData} />}
        </FlexBox>
    )
}